import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { checkRateLimit, resetRateLimit } from '../utils/rateLimit';
import { supabase } from '../config/supabase';

interface User {
  email: string;
  id?: string;
  isAdmin?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string; remainingAttempts?: number; lockedUntil?: number }>;
  logout: () => Promise<void> | void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean; // Add loading state
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = 'fusion_mart_auth';

// Check if user has admin role
const checkAdminRole = async (emailOrId: string): Promise<boolean> => {
    try {
      // For backward compatibility, check if it's the old admin email FIRST (fastest check)
      if (emailOrId === 'fusionmart.cht2019@gmail.com') {
        return true;
      }

      // Then check user metadata (most reliable)
      try {
        const { data: { user: supabaseUser } } = await supabase.auth.getUser();
        if (supabaseUser?.user_metadata?.role === 'admin') {
          return true;
        }
      } catch (metadataError) {
        // Silently handle metadata check errors
      }

      // Check users table in Supabase (if it exists and is properly configured)
      // This is optional - only check if the table exists
      try {
        const { data, error } = await supabase
          .from('users')
          .select('role')
          .or(`email.eq.${emailOrId},id.eq.${emailOrId}`)
          .eq('role', 'admin')
          .maybeSingle(); // Use maybeSingle() instead of single() to avoid errors if no row found

        if (!error && data && data.role === 'admin') {
          return true;
        }
      } catch (tableError: any) {
        // Table might not exist or query might fail - that's okay
      }

      return false;
    } catch (error) {
      // For backward compatibility, grant admin access to the known admin email
      return emailOrId === 'fusionmart.cht2019@gmail.com';
    }
  };

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    
    // Helper function to set user data
    const setUserData = async (sessionUser: any): Promise<void> => {
      if (!isMounted) return;
      
      try {
        const isAdmin = await checkAdminRole(sessionUser.email || sessionUser.id);
        
        if (!isMounted) return;
        
        const userData: User = {
          email: sessionUser.email || '',
          id: sessionUser.id,
          isAdmin
        };
        setUser(userData);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
      } catch (error) {
        throw error;
      }
    };

    // Check for existing Supabase session on mount
    const checkSession = async (): Promise<void> => {
      if (!isMounted) return;
      
      setIsLoading(true);
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          // Fallback to localStorage
          const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
          if (storedAuth && isMounted) {
            try {
              const authData = JSON.parse(storedAuth);
              setUser(authData);
            } catch (parseError) {
              // Silently handle parse errors
            }
          }
          if (isMounted) setIsLoading(false);
          return;
        }

        if (session?.user && isMounted) {
          try {
            await setUserData(session.user);
          } catch (error) {
            // Silently handle errors
          } finally {
            if (isMounted) setIsLoading(false);
          }
        } else if (isMounted) {
          setIsLoading(false);
          // Fallback to localStorage
          const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
          if (storedAuth) {
            try {
              const authData = JSON.parse(storedAuth);
              if (isMounted) setUser(authData);
            } catch (parseError) {
              // Silently handle parse errors
            }
          }
        }
      } catch (error) {
        // Fallback to localStorage
        if (isMounted) {
          try {
            const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
            if (storedAuth) {
              const authData = JSON.parse(storedAuth);
              setUser(authData);
            }
          } catch (localError) {
            // Silently handle errors
          }
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    checkSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip if component is unmounted
      if (!isMounted) return;
      
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
        if (!isMounted) return;
        
        try {
          setIsLoading(true);
          const isAdmin = await checkAdminRole(session.user.email || session.user.id);
          
          if (!isMounted) {
            setIsLoading(false);
            return;
          }
          
          const userData: User = {
            email: session.user.email || '',
            id: session.user.id,
            isAdmin
          };
          setUser(userData);
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
          setIsLoading(false);
        } catch (error) {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      } else if (event === 'SIGNED_OUT') {
        if (!isMounted) return;
        setUser(null);
        localStorage.removeItem(AUTH_STORAGE_KEY);
        setIsLoading(false);
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        if (!isMounted) return;
        // Refresh user data when token is refreshed
        const isAdmin = await checkAdminRole(session.user.email || session.user.id);
        const userData: User = {
          email: session.user.email || '',
          id: session.user.id,
          isAdmin
        };
        setUser(userData);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string; remainingAttempts?: number; lockedUntil?: number }> => {
    // Check rate limit before attempting login
    const rateLimitCheck = checkRateLimit('login', email);
    if (!rateLimitCheck.allowed) {
      return {
        success: false,
        message: rateLimitCheck.message || 'Too many login attempts. Please try again later.',
        remainingAttempts: rateLimitCheck.remainingAttempts,
        lockedUntil: rateLimitCheck.lockedUntil,
      };
    }
    
    try {
      // Try Supabase Auth first
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim()
      });

      if (authError) {
        return {
          success: false,
          message: authError.message || 'Invalid email or password',
        };
      }

      if (authData?.user && authData?.session) {
        // Reset rate limit on successful login
        resetRateLimit('login', email);
        
        // Check if user is admin
        const isAdmin = await checkAdminRole(authData.user.email || authData.user.id);
        
        const userData: User = {
          email: authData.user.email || email,
          id: authData.user.id,
          isAdmin
        };
        
        setUser(userData);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
        
        // Wait a bit to ensure session is persisted
        await new Promise(resolve => setTimeout(resolve, 100));
        
        return { success: true };
      } else if (authData?.user && !authData?.session) {
        return {
          success: false,
          message: 'Login successful but session could not be created. Please try again.',
        };
      }
    } catch (error: any) {
      // Fallback to old hardcoded credentials for backward compatibility
      if (email === 'fusionmart.cht2019@gmail.com' && password === 'admin123') {
        // Reset rate limit on successful login
        resetRateLimit('login', email);
        const userData: User = {
          email,
          isAdmin: true
        };
        setUser(userData);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
        return { success: true };
      }
      
      return {
        success: false,
        message: error?.message || 'An error occurred during login. Please try again.',
      };
    }
    
    // Failed login
    return {
      success: false,
      message: 'Invalid email or password',
    };
  };

  const logout = async (): Promise<void> => {
    try {
      // Sign out from Supabase
      await supabase.auth.signOut();
    } catch (error) {
      // Silently handle sign out errors
    } finally {
      // Clear local state
      setUser(null);
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  };

  const isAuthenticated = !!user;
  const isAdmin = !!(user && user.isAdmin);

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated, isAdmin, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
